/**
 * BigCommerce Integration Factory
 * Creates and configures BigCommerce integration instances
 */

import { BigCommerceIntegration } from './BigCommerceIntegration';
import { IntegrationConfig, ValidationResult } from '../../../types/integration';
import { IntegrationFactory } from '../registry/IntegrationRegistry';
import { BaseIntegration } from '../base/BaseIntegration';

export interface BigCommerceConnectionParams {
  storeHash: string;
  accessToken: string;
  clientId?: string;
  clientSecret?: string;
  apiVersion?: 'v2' | 'v3';
  webhookSecret?: string;
  enableWebhooks?: boolean;
}

export class BigCommerceIntegrationFactory implements IntegrationFactory {
  /**
   * Create a new BigCommerce integration instance
   */
  static create(params: BigCommerceConnectionParams): BigCommerceIntegration {
    const config: IntegrationConfig = {
      id: `bigcommerce_${Date.now()}`,
      name: 'BigCommerce Integration',
      type: 'ecommerce',
      platform: 'bigcommerce',
      status: 'inactive',
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 60,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {
          storeHash: params.storeHash,
          accessToken: params.accessToken,
          clientId: params.clientId,
          clientSecret: params.clientSecret,
          apiVersion: params.apiVersion || 'v3',
          webhookSecret: params.webhookSecret,
          enableWebhooks: params.enableWebhooks || false
        }
      },
      credentials: {
        encrypted: JSON.stringify({ 
          accessToken: params.accessToken,
          clientId: params.clientId,
          clientSecret: params.clientSecret
        }),
        keyId: 'default',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      },
      fieldMappings: [
        {
          id: 'product_id_mapping',
          sourceField: 'id',
          targetField: 'product_id',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'product_name_mapping',
          sourceField: 'name',
          targetField: 'title',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'customer_email_mapping',
          sourceField: 'email',
          targetField: 'customer_email',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        }
      ],
      webhookConfig: params.webhookSecret ? {
        url: '',
        events: [
          { type: 'store/product/created', description: 'Triggered when a new product is created', payload: {} },
          { type: 'store/product/updated', description: 'Triggered when a product is updated', payload: {} },
          { type: 'store/order/created', description: 'Triggered when a new order is placed', payload: {} }
        ],
        secret: params.webhookSecret,
        retryPolicy: {
          maxAttempts: 3,
          baseDelay: 1000,
          maxDelay: 30000,
          backoffMultiplier: 2,
          retryableErrors: ['NETWORK_ERROR', 'TIMEOUT', 'RATE_LIMIT']
        },
        enabled: true
      } : undefined,
      syncSchedule: {
        enabled: true,
        frequency: 'hourly',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: []
      },
      metadata: {
        description: 'BigCommerce e-commerce integration',
        tags: ['ecommerce', 'bigcommerce', 'sync'],
        category: 'E-commerce',
        version: '1.0.0',
        supportedFeatures: ['product_sync', 'customer_sync', 'order_sync', 'inventory_management', 'webhook_support'],
        limitations: ['Requires access token', 'Rate limited by BigCommerce API'],
        documentation: '/docs/integrations/bigcommerce',
        customData: {}
      }
    };

    return new BigCommerceIntegration(config);
  }

  /**
   * Create integration from existing configuration
   */
  static fromConfig(config: IntegrationConfig): BigCommerceIntegration {
    return new BigCommerceIntegration(config);
  }

  /**
   * Validate BigCommerce connection parameters
   */
  static validateParams(params: BigCommerceConnectionParams): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!params.storeHash) {
      errors.push('Store hash is required');
    } else if (!/^[a-z0-9]{10}$/.test(params.storeHash)) {
      errors.push('Store hash must be a 10-character alphanumeric string');
    }

    if (!params.accessToken) {
      errors.push('Access token is required');
    } else if (params.accessToken.length < 20) {
      errors.push('Access token appears to be invalid (too short)');
    }

    if (params.apiVersion && !['v2', 'v3'].includes(params.apiVersion)) {
      errors.push('API version must be either v2 or v3');
    }

    if (params.webhookSecret && params.webhookSecret.length < 16) {
      errors.push('Webhook secret must be at least 16 characters long');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Test connection with the provided parameters
   */
  static async testConnection(params: BigCommerceConnectionParams): Promise<{ success: boolean; error?: string; storeInfo?: any }> {
    const validation = this.validateParams(params);
    if (!validation.isValid) {
      return {
        success: false,
        error: `Invalid parameters: ${validation.errors.join(', ')}`
      };
    }

    try {
      const baseUrl = `https://api.bigcommerce.com/stores/${params.storeHash}/${params.apiVersion || 'v3'}`;
      const response = await fetch(`${baseUrl}/store`, {
        headers: {
          'X-Auth-Token': params.accessToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(params.clientId && { 'X-Auth-Client': params.clientId })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const storeInfo = await response.json();
      
      return {
        success: true,
        storeInfo: storeInfo.data || storeInfo
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Instance methods required by IntegrationFactory interface
  
  async create(config: IntegrationConfig): Promise<BaseIntegration> {
    return BigCommerceIntegrationFactory.fromConfig(config);
  }

  async validate(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    const customSettings = config.settings?.customSettings as BigCommerceConnectionParams;
    
    if (!customSettings) {
      return {
        valid: false,
        errors: [{ field: 'settings', message: 'BigCommerce configuration is required', code: 'REQUIRED' }],
        warnings: []
      };
    }

    const validation = BigCommerceIntegrationFactory.validateParams(customSettings);
    
    return {
      valid: validation.isValid,
      errors: validation.errors.map(error => ({ field: 'config', message: error, code: 'VALIDATION_ERROR' })),
      warnings: []
    };
  }

  getConfigSchema(): Record<string, any> {
    return {
      type: 'object',
      properties: {
        storeHash: {
          type: 'string',
          title: 'Store Hash',
          description: 'Your BigCommerce store hash (10-character alphanumeric string)',
          pattern: '^[a-z0-9]{10}$',
          minLength: 10,
          maxLength: 10
        },
        accessToken: {
          type: 'string',
          title: 'Access Token',
          description: 'API access token for authentication',
          minLength: 20
        },
        apiVersion: {
          type: 'string',
          title: 'API Version',
          description: 'BigCommerce API version to use',
          enum: ['v2', 'v3'],
          default: 'v3'
        }
      },
      required: ['storeHash', 'accessToken']
    };
  }

  getSupportedFeatures(): string[] {
    return [
      'product_sync',
      'customer_sync',
      'order_sync',
      'inventory_management',
      'webhook_support',
      'real_time_sync',
      'batch_operations',
      'field_mapping',
      'conflict_resolution'
    ];
  }
}